<template>
  <div
    class="flex flex-col p-5 pb-3 gap-4 rounded-xl bg-bg-1 min-h-[124px] relative"
    :class="containerClasses"
  >
    <div class="flex justify-between items-center">
      <div class="flex items-center gap-1">
        <div :class="labelClasses">
          {{ label }}
        </div>
        <base-popover
          v-if="showPopover"
          placement="top"
          popover-type="tooltip"
          trigger="hover"
        >
          <template #trigger>
            <div class="cursor-pointer text-primary-main">
              <svg-icon name="ic-info-tooltip" />
            </div>
          </template>
          <template #content>
            <div class="text-color-4 text-sm font-medium">
              {{ popoverContent }}
            </div>
          </template>
        </base-popover>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-level-7 text-color-3 font-medium text-right">
          {{ formatAmount(maxAmount) }}
        </span>
        <button
          class="text-level-7 text-brand font-medium"
          @click="setMaxAmount"
        >
          {{ $t('AmountInput | max') }}
        </button>
      </div>
    </div>

    <div class="flex items-center justify-between">
      <div class="flex flex-col amount-input-container">
        <input
          :value="modelValue"
          type="text"
          inputmode="decimal"
          class="flex-1 bg-transparent text-xl font-semibold text-color-1 outline-none placeholder:text-color-3 number-input"
          :placeholder="placeholder"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />
        <div
          v-if="showFlexibleAmountInfo"
          class="overflow-hidden text-ellipsis whitespace-nowrap text-xs font-medium leading-[150%] min-h-[18px] mt-1"
          :class="flexibleAmountTextClasses"
        >
          <template v-for="(part, index) in flexibleAmountParts" :key="index">
            <span
              v-if="part.amount"
              class="underline cursor-pointer hover:opacity-80 transition-opacity"
              @click="emits('update:modelValue', part.amount)"
            >
              {{ part.text }}
            </span>
            <span v-else>{{ part.text }}</span>
          </template>
        </div>
      </div>

      <div class="flex items-center">
        <coin-dropdown
          v-if="coinsList && coinsList.length > 0"
          class="coin-selector"
          :model-value="selectedCoinId"
          :list="formattedCoinsForDropdown"
          :label="false"
          :show-active-balance="false"
          @update:modelValue="onCoinChange"
        />

        <div
          v-else
          class="flex items-center gap-2 px-2 py-2 rounded-2.5 bg-background-color-3"
        >
          <img
            v-if="currencyCoinIcon"
            :src="currencyCoinIcon"
            alt=""
            class="w-6 h-6 rounded-full"
          />
          <svg-icon v-else name="ic-tether" class="!w-[24px] !h-[24px]" />
          <span class="text-sm font-medium text-color-1 uppercase">
            {{ currency }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { BasePopover } from '@emcd-shared/base-popover'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import { CoinDropdown } from '@/features/CoinDropdown'

const { t: $t } = useI18n()
const store = useStore()

const currencyCoinIcon = computed(() => {
  const coins = store.state.coinService?.coins
  return coins?.[props.currency?.toLowerCase()]?.media_url ?? null
})

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },
  label: {
    type: String,
    default: 'amount_input|label|payment_amount',
  },
  placeholder: {
    type: String,
    default: '10 000.00',
  },
  currency: {
    type: String,
    default: 'USDT',
  },
  maxAmount: {
    type: [String, Number],
    default: 100000,
  },
  showPopover: {
    type: Boolean,
    default: false,
  },
  popoverContent: {
    type: String,
    default: '',
  },
  labelVariant: {
    type: String,
    default: 'default', // 'default' | 'muted'
    validator: (value) => ['default', 'muted'].includes(value),
  },
  showFlexibleAmountInfo: {
    type: Boolean,
    default: false,
  },
  flexibleAmount: {
    type: Boolean,
    default: false,
  },
  minAmount: {
    type: [String, Number],
    default: null,
  },
  coinsList: {
    type: Array,
    default: () => [],
  },
  selectedCoinId: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  isOneTime: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['update:modelValue', 'focus', 'blur', 'update:coin'])

const isMaxAmountExceeded = computed(() => {
  if (!props.modelValue || !props.maxAmount) return false
  return parseFloat(props.modelValue) > parseFloat(props.maxAmount)
})

const isMinAmountNotReached = computed(() => {
  if (!props.modelValue || !props.minAmount) return false
  return parseFloat(props.modelValue) < parseFloat(props.minAmount)
})

const containerClasses = computed(() => {
  return props.hasError ? 'border border-[#E53935]' : 'border border-color-35'
})

const labelClasses = computed(() => {
  const variants = {
    default: 'text-level-7 text-color-1 font-medium',
    muted:
      'overflow-hidden text-color-3 text-ellipsis text-sm font-medium leading-[150%]',
  }
  return variants[props.labelVariant] || variants.default
})

const flexibleAmountTextClasses = computed(() => {
  return props.hasError ? 'text-[#E53935]' : 'text-color-3'
})

const formattedCoinsForDropdown = computed(() => {
  return props.coinsList.map((coin) => ({
    coin_id: coin.optionValue,
    coin: {
      id: coin.optionValue,
      title: coin.optionLabel,
      description: coin.optionLabel,
      media_url: coin.media_url,
    },
    wallet_balance: 0,
    text: coin.optionLabel,
    value: coin.optionValue,
  }))
})

const activeCoin = computed(() => {
  if (!props.selectedCoinId || !props.coinsList.length) return null
  return props.coinsList.find(
    (coin) => coin.optionValue === props.selectedCoinId
  )
})

const flexibleAmountText = computed(() => {
  const coinLabel = activeCoin.value?.optionLabel || props.currency

  if (props.hasError && isMaxAmountExceeded.value) {
    return $t('amount_input|text|maximum_amount', {
      amount: formatAmountForRange(props.maxAmount, coinLabel),
      currency: coinLabel.toUpperCase(),
    })
  }

  if (props.hasError && isMinAmountNotReached.value) {
    return $t('amount_input|text|minimum_amount', {
      amount: formatAmountForRange(props.minAmount, coinLabel),
      currency: coinLabel.toUpperCase(),
    })
  }

  if (props.flexibleAmount && props.minAmount !== null && props.modelValue) {
    const min = formatAmountForRange(props.minAmount, coinLabel)
    const max = formatAmountForRange(props.modelValue, coinLabel)
    return $t('amount_input|text|amount_range', {
      min,
      max,
      currency: coinLabel.toUpperCase(),
    })
  }

  if (props.isOneTime) {
    return ''
  }

  return ''
})

const flexibleAmountParts = computed(() => {
  const text = flexibleAmountText.value
  return text.split(/(\d+\.?\d*)/).map((part) => ({
    text: part,
    amount: /^\d+\.?\d*$/.test(part) ? part : null,
  }))
})

function onInput(event) {
  let value = event.target.value.replace(/[^0-9.]/g, '')

  const parts = value.split('.')
  if (parts.length > 2) {
    value = parts[0] + '.' + parts.slice(1).join('')
  }

  event.target.value = value
  emits('update:modelValue', value)
}

function onFocus(event) {
  emits('focus', event)
}

function onBlur(event) {
  emits('blur', event)
}

function setMaxAmount() {
  emits('update:modelValue', props.maxAmount.toString())
}

function formatAmount(amount) {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0.00'

  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

function formatAmountForRange(amount, coinLabel) {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0'

  const coin = (coinLabel || props.currency).toUpperCase()
  const decimals = coin === 'USDT' ? 2 : 6

  return Number(num).toFixed(decimals)
}

function onCoinChange(coinId) {
  emits('update:coin', coinId)
}
</script>

<style scoped lang="scss">
.number-input::-webkit-outer-spin-button,
.number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.number-input[type='number'] {
  appearance: textfield;
  -moz-appearance: textfield;
  color: var(--text-4);
}

@media screen and (max-width: 395px) {
  .amount-input-container {
    max-width: 165px;
  }
}
@media screen and (max-width: 375px) {
  .amount-input-container {
    max-width: 170px;
  }
}
:deep(.coin-selector) {
  .coin-dropdown {
    position: static;

    .base-select--activator {
      background-color: var(--bg-35);
      max-height: 36px;
      gap: 8px;
      padding: 6px 8px;
      min-width: 92px;

      .coin-dropdown__main {
        min-width: 40px;
      }

      .coin-dropdown__main-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
      }
    }

    .base-select--activator:hover {
      background-color: var(--bg-4);
    }

    .base-select--main {
      left: 5%;
      right: 21px;
      top: 52%;
      z-index: 70;
      max-height: 336px;

      @media screen and (max-width: 495px) {
        left: 0;
      }

      ul {
        max-height: calc(310px - 60px);
      }
    }
  }
}
</style>
